import { db } from 'config/firebase.js';
import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import useLocalStorage, { clearLocalStorage, storageAvailable } from './useLocalStorage';
import { set as idbSet, get as idbGet, setMany, values as getValues, clear as eraseDb } from 'idb-keyval';

const CACHE_META_KEY = '_merlin_cache_meta';

/**
 * This Hook is used to get the fuzzy search data from firebase and store it in local storage
 * Then periodocially update the local data if the ttl has expired
 * @param {Bool} expired 
 * @param {String} org 
 * @returns 
 */
function useSearchData(expired) {

    /** TODO - change for production */
    const ttlKeyName = 'merlinSearchTtl14';
    const searchSkusName = 'mspid14';
    /** TODO - sync ttl with backend hydration */
    // const ttl = 1000 * 60 * 60 * 8; // 8 hours
    const ttl = 1000 * 60 * 60 * 24 * 7; // 1 week

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [state, setState] = useState(false);
    const [message, setMessage] = useState('useSearchData Hook Initialized');
    const [localDataExpiration, setLocalExpiration] = useLocalStorage(ttlKeyName, new Date().getTime());

    async function getFirebaseChoices(org) {
        try {
            const collectionRef = collection(db, "fuzzySearch", org, "fuzzyChoices");
            const choicesQuery = state
                ? query(collectionRef, where("disc", "!=", true))
                : query(collectionRef);

            const choicesSnapshot = await getDocs(choicesQuery);

            const snapSize = choicesSnapshot.size;
            setMessage(`Identified ${snapSize} products. Please wait.`);
            const choices = [];
            const skus = [];
            choicesSnapshot.forEach((doc) => {
                skus.push(doc.id)
                choices.push({ ...doc.data() })
            })
            //setMerlinSearchSkus(skus);
            setMessage(`Product Data Loaded. Please wait.`);
            return choices;
        } catch (error) {
            console.log(error);
            setError(error);
        }
    }

    function chunkArray(array, size) {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
            const chunk = array.slice(i, i + size);
            result.push(chunk);
        }
        return result;
    }

    async function saveManyLocalData(choices) {
        if (!choices || !Array.isArray(choices) || choices.length === 0) {
            console.warn('saveManyLocalData: No valid choices to save');
            return;
        }
        const chunks = chunkArray(choices, 100);
        const numChunks = chunks.length;

        await eraseDb();

        for (let i = 0; i < chunks.length; i++) {
            const chunk = chunks[i];
            const structured = chunk.map((choice) => [choice.sku, choice]);
            try {
                await setMany(structured);
                const percent = Math.round(((i + 1) / numChunks) * 100);
                setMessage(`Local application data loading ${percent}% complete. Please wait.`);
            } catch (error) {
                console.error('Error saving chunk:', error);
                console.log([...structured]);
            }
        }

        // Store metadata for future cache validation
        await idbSet(CACHE_META_KEY, {
            count: choices.length,
            timestamp: Date.now(),
            version: 14
        });
    }

    async function getManyLocalData() {
        try {
            // getValues returns an array of values for products
            const localChoices = await getValues();
            const validChoices = localChoices.filter((choice) => choice !== undefined && choice?.sku);

            // Get expected count from metadata
            const meta = await idbGet(CACHE_META_KEY);
            const expectedCount = meta?.count || 0;

            // Calculate completeness ratio
            const completeness = expectedCount > 0 ? validChoices.length / expectedCount : 0;
            const missingCount = expectedCount - validChoices.length;

            // Only refetch if significantly incomplete (>5% missing) or no valid data
            if (completeness < 0.95 || validChoices.length === 0) {
                const percentComplete = Math.round(completeness * 100);
                setMessage(`Cache ${percentComplete}% complete (${missingCount} missing), refreshing data...`);
                const firebaseChoices = await getFirebaseChoices('topicz');
                await saveManyLocalData(firebaseChoices);
                setMessage('Local Application Data Loaded');
                return firebaseChoices || [];
            } else {
                // Use what we have if mostly complete
                if (validChoices.length < localChoices.length) {
                    console.warn(`Using ${validChoices.length} valid items (${localChoices.length - validChoices.length} undefined filtered out)`);
                }
                setMessage('Local Application Data Loaded');
                return validChoices;
            }

        } catch (error) {
            console.error('getManyLocalData error:', error);
            return [];
        }
    }

    // function creates an timer to delay setLoading(false) until the message has been displayed
    function delaySetLoading() {
        return setTimeout(() => {
            setLoading(false);
        }, 800)
    }

    useEffect(() => {
        setLoading(true)
        const populateData = async () => {
            try {
                const rightNow = new Date().getTime();
                if (rightNow - localDataExpiration > ttl || expired) {
                    // the data is stale
                    // update local storage
                    setMessage('Getting the latest data for you. Please wait.')
                    setLocalExpiration(new Date().getTime());
                    const choices = await getFirebaseChoices('topicz');
                    setData(choices || []);
                    await saveManyLocalData(choices);
                    delaySetLoading();
                } else {
                    // the data is fresh
                    // use local storage
                    setMessage('Attempting to load local data. Please wait.')
                    const choices = await getManyLocalData();
                    setData(choices || []);
                    delaySetLoading();
                }
            } catch (error) {
                console.log(error);
                setError(error);
                setLoading(false);
            }
        }
        populateData();
    }, []);

    return [data, loading, error, setState, message]
}

export default useSearchData;